import { ChainId } from '@nevellusdex/core-sdk'
import { Chef } from 'app/features/onsen/enum'
import {
  getEnceladusFarms,
  getEnceladusPairAddreses,
  getEnceladusPoolHistories,
  getEnceladusPools,
  getEnceladusNvlsPerBlock,
  getEnceladusTotalAllocPoint,
  getEnceladusV2Farms,
  getEnceladusV2PairAddreses,
  getEnceladusV2Pools,
  getDioneFarms,
  getDioneFarmsWithUser,
  getDionePairAddreses,
} from 'app/services/graph/fetchers'
import { useActiveWeb3React } from 'app/services/web3'
import concat from 'lodash/concat'
import { useMemo } from 'react'
import useSWR, { SWRConfiguration } from 'swr'

export function useEnceladusTotalAllocPoint(swrConfig = undefined) {
  const { chainId } = useActiveWeb3React()
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  return useSWR(shouldFetch ? 'enceladusTotalAllocPoint' : null, () => getEnceladusTotalAllocPoint(), swrConfig)
}

export function useEnceladusNvlsPerBlock(swrConfig = undefined) {
  const { chainId } = useActiveWeb3React()
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  return useSWR(shouldFetch ? 'enceladusNvlsPerBlock' : null, () => getEnceladusNvlsPerBlock(), swrConfig)
}

interface useFarmsProps {
  chainId: number
  swrConfig?: SWRConfiguration
}

export function useEnceladusFarms({ chainId, swrConfig = undefined }: useFarmsProps) {
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  const { data } = useSWR(shouldFetch ? ['enceladusFarms'] : null, () => getEnceladusFarms(undefined), swrConfig)
  return useMemo(() => {
    if (!data) return []
    // @ts-ignore TYPE NEEDS FIXING
    return data.map((data) => ({ ...data, chef: Chef.ENCELADUS }))
  }, [data])
}

export function useEnceladusV2Farms({ chainId, swrConfig = undefined }: useFarmsProps) {
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  const { data } = useSWR(shouldFetch ? 'enceladusV2Farms' : null, () => getEnceladusV2Farms(), swrConfig)
  return useMemo(() => {
    if (!data) return []
    // @ts-ignore TYPE NEEDS FIXING
    return data.map((data) => ({ ...data, chef: Chef.ENCELADUS_V2 }))
  }, [data])
}

export function useDioneFarms({ chainId, swrConfig = undefined }: useFarmsProps) {
  const shouldFetch =
    chainId &&
    [
      ChainId.MATIC,
      ChainId.MATIC_TESTNET,
      ChainId.XDAI,
      ChainId.HARMONY,
      ChainId.CELO,
      ChainId.FUSE,
    ].includes(chainId)
  const { data } = useSWR(
    shouldFetch ? ['dioneFarms', chainId] : null,
    (_, chainId) => getDioneFarms(chainId),
    swrConfig
  )
  return useMemo(() => {
    if (!data) return []
    // @ts-ignore TYPE NEEDS FIXING
    return data.map((data) => ({ ...data, chef: Chef.DIONE }))
  }, [data])
}

export function useFarms({ chainId, swrConfig = undefined }: useFarmsProps) {
  const enceladusFarms = useEnceladusFarms({ chainId })
  const enceladusV2Farms = useEnceladusV2Farms({ chainId })
  const dioneFarms = useDioneFarms({ chainId })
  return useMemo(
    () => concat(enceladusFarms, enceladusV2Farms, dioneFarms).filter((pool) => pool && pool.pair),
    [enceladusFarms, enceladusV2Farms, dioneFarms]
  )
}

export function useEnceladusPairAddresses() {
  const { chainId } = useActiveWeb3React()
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  return useSWR(shouldFetch ? ['enceladusPairAddresses', chainId] : null, (_) => getEnceladusPairAddreses())
}

export function useEnceladusV2PairAddresses() {
  const { chainId } = useActiveWeb3React()
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  return useSWR(shouldFetch ? ['enceladusV2PairAddresses', chainId] : null, (_) => getEnceladusV2PairAddreses())
}

export function useDionePairAddresses() {
  const { chainId } = useActiveWeb3React()
  const shouldFetch =
    chainId &&
    [
      ChainId.MATIC,
      ChainId.MATIC_TESTNET,
      ChainId.XDAI,
      ChainId.HARMONY,
      ChainId.CELO,
      ChainId.FUSE,
    ].includes(chainId)
  return useSWR(shouldFetch ? ['dionePairAddresses', chainId] : null, (_, chainId) =>
    getDionePairAddreses(chainId)
  )
}

export function useFarmPairAddresses() {
  const { data: enceladusPairAddresses } = useEnceladusPairAddresses()
  const { data: enceladusV2PairAddresses } = useEnceladusV2PairAddresses()
  const { data: dionePairAddresses } = useDionePairAddresses()
  return useMemo(
    () => concat(enceladusPairAddresses ?? [], enceladusV2PairAddresses ?? [], dionePairAddresses ?? []),
    [enceladusPairAddresses, enceladusV2PairAddresses, dionePairAddresses]
  )
}

export function useEnceladusFarmsWithUsers({
  chainId = ChainId.GÖRLI,
  variables,
}: {
  chainId: ChainId
  variables: any
}) {
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  const { data } = useSWR(shouldFetch ? ['enceladusPools', chainId, variables] : null, (_) =>
    getEnceladusPools(chainId, variables)
  )
  return useMemo(() => {
    if (!data) return []
    // @ts-ignore TYPE NEEDS FIXING
    return data.map((data) => ({ ...data, chef: Chef.ENCELADUS }))
  }, [data])
}

export function useEnceladusV2FarmsWithUsers({
  chainId = ChainId.GÖRLI,
  variables,
}: {
  chainId: ChainId
  variables: any
}) {
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  const { data } = useSWR(shouldFetch ? ['enceladusV2Pools', chainId, variables] : null, (_) =>
    getEnceladusV2Pools(chainId, variables)
  )
  return useMemo(() => {
    if (!data) return []
    // @ts-ignore TYPE NEEDS FIXING
    return data.map((data) => ({ ...data, chef: Chef.ENCELADUS_V2 }))
  }, [data])
}

export function useDioneFarmsWithUser({
  chainId,
  variables,
  swrConfig = undefined,
}: {
  chainId: ChainId
  variables: any
  swrConfig?: SWRConfiguration | undefined
}) {
  const shouldFetch =
    chainId &&
    [
      ChainId.MATIC,
      ChainId.MATIC_TESTNET,
      ChainId.XDAI,
      ChainId.HARMONY,
      ChainId.CELO,
      ChainId.FUSE,
    ].includes(chainId)
  const { data } = useSWR(
    shouldFetch ? ['dioneFarmsWithUser', chainId, variables] : null,
    (_, chainId) => getDioneFarmsWithUser(chainId),
    swrConfig
  )
  return useMemo(() => {
    if (!data) return []
    // @ts-ignore TYPE NEEDS FIXING
    return data.map((data) => ({ ...data, chef: Chef.DIONE }))
  }, [data])
}

export function useFarmsWithUsers({ chainId = ChainId.GÖRLI, variables }: { chainId: ChainId; variables: any }) {
  const enceladusPools = useEnceladusFarmsWithUsers({ chainId, variables })
  const enceladusV2Pools = useEnceladusV2FarmsWithUsers({ chainId, variables })
  const dioneFarms = useDioneFarmsWithUser({ chainId, variables })
  return useMemo(
    () =>
      concat(enceladusPools ?? [], enceladusV2Pools ?? [], dioneFarms ?? []).filter((pool) => pool && pool.pair),
    [enceladusPools, enceladusV2Pools, dioneFarms]
  )
}

export function useEnceladusFarmHistories({
  chainId = ChainId.GÖRLI,
  variables,
}: {
  chainId: ChainId
  variables: any
}) {
  const shouldFetch = chainId && chainId === ChainId.GÖRLI
  return useSWR(shouldFetch ? ['enceladusPoolHistories', chainId, variables] : null, (_) =>
    getEnceladusPoolHistories(chainId, variables)
  )
}

export function useFarmHistories({ chainId = ChainId.GÖRLI, variables }: { chainId: ChainId; variables: any }) {
  const { data: enceladusPoolHistories } = useEnceladusFarmHistories({ chainId, variables })
  return useMemo(() => concat(enceladusPoolHistories ?? []), [enceladusPoolHistories])
}
